import { Plugin } from 'ckeditor5/src/core';
import { Notification } from 'ckeditor5/src/ui';
import { FileRepository } from 'ckeditor5/src/upload';
import FileUploadCommand from './fileuploadcommand';
const isFile = (modelElement) => {
    return !!modelElement && modelElement.is('element', 'file');
};
export default class FileUploadEditing extends Plugin {
    static get requires() {
        return [FileRepository, Notification];
    }
    static get pluginName() {
        return 'FileUploadEditing';
    }
    constructor(editor) {
        super(editor);
        this._uploadFileElements = new Map();
    }
    init() {
        const editor = this.editor;
        const doc = editor.model.document;
        const conversion = editor.conversion;
        const fileRepository = editor.plugins.get(FileRepository);
        const fileUploadConfig = editor.config.get('fileUpload');
        const onPasteFileUpload = fileUploadConfig && fileUploadConfig.onPasteFileUpload ?
            fileUploadConfig.onPasteFileUpload : undefined;
        const command = new FileUploadCommand(editor);
        this.listenTo(editor.plugins.get('ClipboardPipeline'), 'inputTransformation', (evt, data) => {
            if (data.method !== 'paste') {
                return;
            }
            const fileUploads = Array.from(editor.editing.view.createRangeIn(data.content))
                .map(value => value.item)
                .filter(viewElement => {
                return viewElement && viewElement.getAttribute && (viewElement.getAttribute('data-file-reference-id') ||
                    viewElement.getAttribute('data-file-element-id'));
            });
            for (const fileUpload of fileUploads) {
                if (onPasteFileUpload) {
                    onPasteFileUpload(fileUpload, evt, data, fileUploads, editor);
                }
            }
        });
        // Prevents from the browser redirecting to the dropped image.
        editor.editing.view.document.on('dragover', (evt, data) => {
            data.preventDefault();
        });
        editor.commands.add('fileUpload', command);
        // Register upcast converter for uploadId.
        conversion.for('upcast').attributeToAttribute({
            view: {
                name: 'file',
                key: 'uploadId'
            },
            model: 'uploadId'
        });
        // Prevents from the browser redirecting to the dropped file.
        editor.editing.view.document.on('dragover', (evt, data) => {
            data.preventDefault();
        });
        // Upload placeholder files that appeared in the model.
        doc.on('change', () => {
            // Note: Reversing changes to start with insertions and only then handle removals. If it was the other way around,
            // loaders for **all** files that land in the $graveyard would abort while in fact only those that were **not** replaced
            // by other files should be aborted.
            const changes = doc.differ
                .getChanges({ includeChangesInGraveyard: true })
                .reverse();
            const insertedFileIds = new Set();
            for (const entry of changes) {
                if (entry.type == 'insert' && entry.name != '$text') {
                    const item = entry.position.nodeAfter;
                    const isInsertedInGraveyard = entry.position.root.rootName == '$graveyard';
                    const elements = Array.from(editor.model.createRangeOn(item))
                        .filter(value => isFile(value.item))
                        .map(value => value.item);
                    for (const fileElement of elements) {
                        // Check if the file element still has upload id.
                        const uploadId = fileElement.getAttribute('uploadId');
                        if (!uploadId) {
                            continue;
                        }
                        // Check if the file is loaded on this client.
                        const loader = fileRepository.loaders.get(uploadId);
                        if (!loader) {
                            continue;
                        }
                        if (isInsertedInGraveyard) {
                            // If the file was inserted to the graveyard for good (**not** replaced by another file),
                            // only then abort the loading process.
                            if (!insertedFileIds.has(uploadId)) {
                                loader.abort();
                            }
                        }
                        else {
                            // Remember the upload id of the inserted file. If it acted as a replacement for another
                            // file (which landed in the $graveyard), the related loader will not be aborted because
                            // this is still the same file upload.
                            insertedFileIds.add(uploadId);
                            // Keep the mapping between the upload ID and the file model element so the upload
                            // can later resolve in the context of the correct model element. The model element could
                            // change for the same upload if one file was replaced by another (e.g. file type was changed),
                            // so this may also replace an existing mapping.
                            this._uploadFileElements.set(uploadId, fileElement);
                            if (loader.status == 'idle') {
                                // If the file was inserted into content and has not been loaded yet, start loading it.
                                this._readAndUpload(loader);
                            }
                        }
                    }
                }
            }
        });
        // Set the default handler for feeding the file element with the requisite attributes.
        this.on('uploadComplete', (evt, { fileElement, data }) => {
            const urls = data.urls ? data.urls : data;
            this.editor.model.change(writer => {
                writer.setAttribute('fileIsOpen', 'false', fileElement);
                // TODO: Remove urls?.default after next version live and stable
                writer.setAttribute('fileUrl', urls.url ? urls.url : urls.default, fileElement);
                if (urls.fileId) {
                    writer.setAttribute('fileId', urls.fileId, fileElement);
                }
            });
        }, { priority: 'low' });
    }
    afterInit() {
        const schema = this.editor.model.schema;
        // Setup schema to allow uploadId and uploadStatus for files.
        // Wait for FileEditing to register first,
        // that's why doing this in afterInit() instead of init().
        if (this.editor.plugins.has('FileEditing')) {
            schema.extend('file', {
                allowAttributes: ['uploadId', 'uploadStatus']
            });
        }
    }
    _readAndUpload(loader) {
        const editor = this.editor;
        const model = editor.model;
        const t = editor.locale.t;
        const fileRepository = editor.plugins.get(FileRepository);
        const notification = editor.plugins.get(Notification);
        const fileUploadElements = this._uploadFileElements;
        const fileElement = fileUploadElements.get(loader.id);
        model.enqueueChange({ isUndoable: false }, writer => {
            writer.setAttribute('uploadStatus', 'reading', fileElement);
        });
        return loader
            .read()
            .then(() => {
            const promise = loader.upload();
            model.enqueueChange({ isUndoable: false }, writer => {
                writer.setAttribute('uploadStatus', 'uploading', fileElement);
            });
            return promise;
        })
            .then(data => {
            model.enqueueChange({ isUndoable: false }, writer => {
                writer.setAttribute('uploadStatus', 'complete', fileElement);
                /**
                 * An event fired when an file is uploaded. You can hook into this event to provide
                 * custom attributes to the {@link module:engine/model/element~Element file element} based on the data from
                 * the server.
                 *
                 * 		const fileUploadEditing = editor.plugins.get( 'fileUploadEditing' );
                 *
                 * 		fileUploadEditing.on( 'uploadComplete', ( evt, { data, fileElement } ) => {
                 * 			editor.model.change( writer => {
                 * 				writer.setAttribute( 'someAttribute', 'foo', fileElement );
                 * 			} );
                 * 		} );
                 *
                 * You can also stop the default handler that sets the `src` and `srcset` attributes
                 * if you want to provide custom values for these attributes.
                 *
                 * 		fileUploadEditing.on( 'uploadComplete', ( evt, { data, fileElement } ) => {
                 * 			evt.stop();
                 * 		} );
                 *
                 * **Note**: This event is fired by the {@link module:file/fileupload/fileuploadediting~fileUploadEditing} plugin.
                 *
                 * @event uploadComplete
                 * @param {Object} data The `uploadComplete` event data.
                 * @param {Object} data.data The data coming from the upload adapter.
                 * @param {module:engine/model/element~Element} data.fileElement The
                 * model {@link module:engine/model/element~Element file element} that can be customized.
                 */
                this.fire('uploadComplete', { data, fileElement });
            });
            clean();
        })
            .catch(error => {
            // If status is not 'error' nor 'aborted' - throw error because it means that something else went wrong,
            // it might be generic error and it would be real pain to find what is going on.
            if (loader.status !== 'error' && loader.status !== 'aborted') {
                throw error;
            }
            // Might be 'aborted'.
            if (loader.status == 'error' && error) {
                notification.showWarning(error, {
                    title: t('Upload failed'),
                    namespace: 'upload'
                });
            }
            // Permanently remove file from insertion batch.
            model.enqueueChange({ isUndoable: false }, writer => {
                writer.remove(fileUploadElements.get(loader.id));
            });
            clean();
        });
        function clean() {
            model.enqueueChange({ isUndoable: false }, writer => {
                writer.removeAttribute('uploadId', fileElement);
                writer.removeAttribute('uploadStatus', fileElement);
                fileUploadElements.delete(loader.id);
            });
            fileRepository.destroyLoader(loader);
        }
    }
}
